import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Plus } from 'lucide-react';
import { staffClient } from 'clients/staff';
import Table, { Column } from 'ui/Table';
import Button from 'ui/Button';
import Modal from 'ui/Modal';
import Input from 'ui/Input';
import Select from 'ui/Select';
import Loading from 'ui/Loading';
import FilterCard, { FilterCardGroup } from 'ui/FilterCard';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import TableContainer from 'ui/TableContainer';
import FilterSection from 'ui/FilterSection';
import { Badge } from 'ui/shadcn';
import {
    SiteCategory,
    SITE_CATEGORY_BADGE,
    SITE_CATEGORY_LABEL,
    SITE_CATEGORY_OPTIONS,
} from 'apis/site';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

type FilterType = 'ALL' | 'IN_PROGRESS' | 'CONTRACT_COMPLETED' | 'COMPLETED_3Y' | 'COMPLETED_5Y';
type CategoryFilter = SiteCategory | 'ALL';

interface ContractInfo {
    revision: number;
    contractDate: string;
    workType?: string;
}

interface Site {
    id: number;
    name: string;
    location?: string;
    type: string;
    category?: SiteCategory;
    visible: boolean;
    chiefSupervisor?: string;
    contractorName?: string;
    workTypes?: string[];
    progressRate?: string;
    contractDate?: string;
    allContracts?: ContractInfo[];
    supervisionStartDate?: string;
    supervisionEndDate?: string;
    totalFloorArea?: string;
    numberOfUnits?: string;
    buildingScale?: string;
    created_at: string;
    updated_at: string;
}

// 현장 상태 → 시맨틱 뱃지 (계약완료 = 대기, 진행중 = 활성, 준공 = 브랜드, 기타 = 무채색)
const SITE_TYPE_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' }> = {
    CONTRACT_COMPLETED: { label: '계약완료', variant: 'warning' },
    IN_PROGRESS: { label: '진행중', variant: 'success' },
    COMPLETED: { label: '준공', variant: 'default' },
    ETC: { label: '기타', variant: 'secondary' },
};

const SiteManagement: React.FC = () => {
    const navigate = useNavigate();
    const [sites, setSites] = useState<Site[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [activeFilter, setActiveFilter] = useState<FilterType>('ALL');
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('ALL');
    const [formData, setFormData] = useState<{
        name: string;
        location: string;
        type: string;
        category: SiteCategory;
        visible: boolean;
    }>({
        name: '',
        location: '',
        type: 'ETC',
        category: 'SUPERVISION',
        visible: true
    });

    const siteTypes = [
        { value: 'CONTRACT_COMPLETED', label: '계약완료' },
        { value: 'IN_PROGRESS', label: '진행중' },
        { value: 'COMPLETED', label: '준공' },
        { value: 'ETC', label: '기타' }
    ];

    useEffect(() => {
        fetchSites();
    }, []);

    const fetchSites = async () => {
        try {
            // limit을 안 주면 서버 기본값 10건만 와서 현장 일부가 목록에서 빠진다
            const response = await staffClient.get('/site', { params: { limit: 1000 } });
            // response.data가 배열인지 확인하고, 아니면 빈 배열 사용
            const sitesData = Array.isArray(response.data) ? response.data :
                           (response.data?.data && Array.isArray(response.data.data)) ? response.data.data :
                           [];
            setSites(sitesData);
        } catch (error) {
            console.error('Failed to fetch sites:', error);
            setSites([]); // 에러 시 빈 배열로 설정
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        try {
            await staffClient.post('/site', formData);
            alert('현장이 생성되었습니다.');
            setShowCreateModal(false);
            fetchSites();
            resetForm();
        } catch (error) {
            console.error('Failed to create site:', error);
            alert('현장 생성 중 오류가 발생했습니다.');
        }
    };

    const handleEdit = (site: Site) => {
        navigate(`/admin/site-management/${site.id}/edit`);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('이 현장을 삭제하시겠습니까?\n관련된 모든 데이터가 함께 삭제됩니다.')) {
            return;
        }

        try {
            await staffClient.delete(`/site/${id}`);
            alert('현장이 삭제되었습니다.');
            fetchSites();
        } catch (error) {
            console.error('Failed to delete site:', error);
            alert('현장 삭제 중 오류가 발생했습니다.');
        }
    };

    const handleToggleVisibility = async (site: Site) => {
        try {
            await staffClient.patch(`/site/${site.id}`, { visible: !site.visible });
            fetchSites();
        } catch (error) {
            console.error('Failed to toggle visibility:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            location: '',
            type: 'ETC',
            category: 'SUPERVISION',
            visible: true
        });
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        resetForm();
    };

    // 준공일 기준 N년 이내 판별
    const isCompletedWithinYears = (site: Site, years: number) => {
        if (site.type !== 'COMPLETED') return false;
        const endDate = site.supervisionEndDate;
        if (!endDate) return false;
        const end = new Date(endDate);
        const cutoff = new Date();
        cutoff.setFullYear(cutoff.getFullYear() - years);
        return end >= cutoff;
    };

    const filterSite = (site: Site, filter: FilterType) => {
        switch (filter) {
            case 'ALL': return true;
            case 'IN_PROGRESS': return site.type === 'IN_PROGRESS';
            case 'CONTRACT_COMPLETED': return site.type === 'CONTRACT_COMPLETED';
            case 'COMPLETED_3Y': return isCompletedWithinYears(site, 3);
            case 'COMPLETED_5Y': return isCompletedWithinYears(site, 5);
            default: return true;
        }
    };

    // 구분(감리/공사/설계) → 상태 순서로 걸러서, 상태 카드 수치도 선택한 구분 안에서만 센다
    const categorySites = categoryFilter === 'ALL'
        ? sites
        : sites.filter(site => (site.category || 'SUPERVISION') === categoryFilter);

    const filteredSites = categorySites.filter(site => filterSite(site, activeFilter));

    const filterCards: { key: FilterType; label: string; count: number }[] = [
        { key: 'ALL', label: '전체', count: categorySites.length },
        { key: 'IN_PROGRESS', label: '진행중', count: categorySites.filter(s => s.type === 'IN_PROGRESS').length },
        { key: 'CONTRACT_COMPLETED', label: '계약완료(미착공)', count: categorySites.filter(s => s.type === 'CONTRACT_COMPLETED').length },
        { key: 'COMPLETED_3Y', label: '3년 내 준공', count: categorySites.filter(s => isCompletedWithinYears(s, 3)).length },
        { key: 'COMPLETED_5Y', label: '5년 내 준공', count: categorySites.filter(s => isCompletedWithinYears(s, 5)).length },
    ];

    const categoryCounts: { key: CategoryFilter; label: string; count: number }[] = [
        { key: 'ALL', label: '전체', count: sites.length },
        ...SITE_CATEGORY_OPTIONS.map(opt => ({
            key: opt.value as CategoryFilter,
            label: opt.label,
            count: sites.filter(s => (s.category || 'SUPERVISION') === opt.value).length,
        })),
    ];

    // 엑셀 다운로드
    const handleExcelDownload = () => {
        const activeLabel = filterCards.find(f => f.key === activeFilter)?.label || '전체';
        const categoryLabel = categoryFilter === 'ALL' ? '전체구분' : SITE_CATEGORY_LABEL[categoryFilter];
        const data = filteredSites.map((site, idx) => ({
            'No': idx + 1,
            '현장명': site.name,
            '구분': SITE_CATEGORY_LABEL[(site.category || 'SUPERVISION') as SiteCategory],
            '상태': getTypeLabel(site.type),
            '시공사': site.contractorName || '',
            '계약일': formatDate(site.contractDate || ''),
            '감리시작': formatDate(site.supervisionStartDate || ''),
            '감리종료': formatDate(site.supervisionEndDate || ''),
            '연면적': site.totalFloorArea || '',
            '세대수': site.numberOfUnits || '',
            '건축규모': site.buildingScale || '',
            '위치': site.location || '',
            '공개': site.visible ? '공개' : '비공개',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '현장목록');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), `현장관리_${categoryLabel}_${activeLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const getTypeLabel = (type: string) => {
        const typeObj = siteTypes.find(t => t.value === type);
        return typeObj ? typeObj.label : type;
    };

    // 날짜 포맷 함수
    const formatDate = (date: string) => {
        if (!date) return '-';
        const d = new Date(date);
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    };

    const columns: Column<Site>[] = [
        {
            key: 'name',
            header: '현장명',
            width: '200px',
            sortable: true,
            render: (value) => <span className="break-keep">{value}</span>
        },
        {
            key: 'category',
            header: '구분',
            width: '72px',
            align: 'center',
            render: (value) => {
                const config = SITE_CATEGORY_BADGE[(value || 'SUPERVISION') as SiteCategory];
                return (
                    <Badge variant={config?.variant || 'secondary'} className="whitespace-nowrap">
                        {config?.label || value || '-'}
                    </Badge>
                );
            }
        },
        {
            key: 'type',
            header: '상태',
            width: '80px',
            render: (value) => {
                const config = SITE_TYPE_BADGE[value] || { label: value || '-', variant: 'secondary' as const };
                return (
                    <Badge variant={config.variant} className="whitespace-nowrap">
                        {config.label}
                    </Badge>
                );
            }
        },
        {
            key: 'contractorName',
            header: '시공사',
            width: '72px',
            render: (value) => <span className="break-keep text-sm">{value || '-'}</span>
        },
        {
            key: 'contractDate',
            header: '계약일',
            width: '108px',
            align: 'left',
            render: (value, row) => {
                const contractCount = row.allContracts?.length || 0;
                const hasMultiple = contractCount > 1;

                return (
                    <div className="flex items-center gap-1 whitespace-nowrap">
                        <span className="text-sm tabular-nums">{formatDate(value)}</span>
                        {hasMultiple && (
                            <div className="relative group">
                                <span className="cursor-default rounded bg-navy-50 px-1.5 text-[10px] font-semibold text-primary">
                                    +{contractCount - 1}
                                </span>
                                <div className="absolute bottom-full left-1/2 z-50 mb-2 hidden w-48 -translate-x-1/2 rounded border border-border bg-card p-2 group-hover:block">
                                    <div className="mb-1 border-b border-border pb-1 text-xs font-semibold text-neutral-700">계약 목록</div>
                                    {row.allContracts?.map((contract, idx) => (
                                        <div key={idx} className="flex justify-between py-0.5 text-xs">
                                            <span className="text-muted-foreground">{contract.revision}차</span>
                                            <span className="tabular-nums text-foreground">{formatDate(contract.contractDate)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'supervisionStartDate',
            header: '감리시작',
            width: '88px',
            align: 'center',
            render: (value) => <span className="whitespace-nowrap text-sm tabular-nums">{formatDate(value)}</span>
        },
        {
            key: 'supervisionEndDate',
            header: '감리종료',
            width: '88px',
            align: 'center',
            render: (value) => <span className="whitespace-nowrap text-sm tabular-nums">{formatDate(value)}</span>
        },
        {
            key: 'totalFloorArea',
            header: '연면적',
            width: '88px',
            align: 'center',
            render: (value) => <span className="whitespace-nowrap text-sm tabular-nums">{value || '-'}</span>
        },
        {
            key: 'numberOfUnits',
            header: '세대수',
            width: '76px',
            align: 'center',
            render: (value) => <span className="whitespace-nowrap text-sm tabular-nums">{value || '-'}</span>
        },
        {
            key: 'buildingScale',
            header: '건축규모',
            width: '128px',
            align: 'center',
            render: (value) => <span className="break-keep text-sm">{value || '-'}</span>
        },
        {
            key: 'visible',
            header: '공개',
            width: '72px',
            align: 'center',
            render: (value, row) => (
                <button
                    type="button"
                    title="클릭하여 공개 여부 전환"
                    className="rounded-full transition-opacity duration-fast hover:opacity-80"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleToggleVisibility(row);
                    }}
                >
                    <Badge variant={value ? 'success' : 'secondary'} className="whitespace-nowrap">
                        {value ? '공개' : '비공개'}
                    </Badge>
                </button>
            )
        }
    ];

    if (loading) {
        return (
            <PageContainer>
                <Loading text="현장 목록을 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="현장 관리"
                subtitle="현장 정보를 관리합니다"
                actions={
                    <Button variant="primary" icon={<Plus />} onClick={() => setShowCreateModal(true)}>
                        현장 추가
                    </Button>
                }
            />

            <FilterSection>
                <label htmlFor="site-category-filter" className="text-sm font-medium text-neutral-700">
                    구분
                </label>
                <Select
                    id="site-category-filter"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value as CategoryFilter)}
                    placeholder=""
                    fullWidth={false}
                    className="w-40"
                    options={categoryCounts.map(c => ({ value: c.key, label: `${c.label} (${c.count})` }))}
                />
                <span className="text-xs text-muted-foreground">
                    감리 · 공사 · 설계 현장을 한 화면에서 관리합니다
                </span>
            </FilterSection>

            <FilterCardGroup columns={5}>
                {filterCards.map((card) => (
                    <FilterCard
                        key={card.key}
                        label={card.label}
                        count={card.count}
                        active={activeFilter === card.key}
                        onClick={() => setActiveFilter(card.key)}
                    />
                ))}
            </FilterCardGroup>

            <TableContainer
                title="현장 목록"
                resultCount={filteredSites.length}
                actions={
                    <Button variant="outline" size="small" icon={<Download />} onClick={handleExcelDownload}>
                        엑셀
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    data={filteredSites}
                    actionsColumnWidth="76px"
                    actions={(site) => (
                        <Button
                            size="small"
                            variant="danger-outline"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(site.id);
                            }}
                        >
                            삭제
                        </Button>
                    )}
                    onRowClick={(site) => handleEdit(site)}
                    pagination
                    pageSize={10}
                />
            </TableContainer>

            {/* Create Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={closeCreateModal}
                title="현장 추가"
                footer={
                    <>
                        <Button variant="outline" onClick={closeCreateModal}>
                            취소
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleCreate}
                            disabled={!formData.name}
                        >
                            추가
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="현장명"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="현장명을 입력하세요"
                    />
                    <Input
                        label="위치"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        placeholder="위치를 입력하세요"
                    />
                    <Select
                        label="구분"
                        required
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value as SiteCategory })}
                        placeholder=""
                        options={SITE_CATEGORY_OPTIONS}
                    />
                    <Select
                        label="상태"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        options={siteTypes}
                    />
                    <Select
                        label="공개 여부"
                        value={formData.visible ? 'true' : 'false'}
                        onChange={(e) => setFormData({ ...formData, visible: e.target.value === 'true' })}
                        options={[
                            { value: 'true', label: '공개' },
                            { value: 'false', label: '비공개' }
                        ]}
                    />
                </div>
            </Modal>

        </PageContainer>
    );
};

export default SiteManagement;
